import { type WebsiteLocale, localeToCountryCode } from '@/types/locales'

import { SESSION_STORAGE_KEYS } from '@/lib/storage-keys'

import { type CheckoutCountry, type CurrencyCode, CHECKOUT_COUNTRIES, CHECKOUT_GB, COUNTRY_CURRENCY } from './types'

// Remember/return which paywall the user entered the checkout funnel from, so the
// checkout back button can route them back. Wrapped because sessionStorage can throw
// a SecurityError when storage is blocked (e.g. private browsing, restricted iframes).
export const storeCheckoutOrigin = (path: string) => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEYS.ZOE_PLUS_CHECKOUT_ORIGIN, path)
  } catch (error) {
    console.error('Failed to write checkout origin to sessionStorage:', error)
  }
}

export const retrieveCheckoutOrigin = (): string | null => {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage.getItem(SESSION_STORAGE_KEYS.ZOE_PLUS_CHECKOUT_ORIGIN)
  } catch (error) {
    console.error('Failed to read checkout origin from sessionStorage:', error)
    return null
  }
}

export const formatCurrency = (
  currency?: CurrencyCode,
  amount?: number,
  stripTrailingZeroes: boolean = false,
  locale?: string,
): string => {
  if (amount === undefined) {
    return ''
  }

  const resolvedLocale = locale || (typeof navigator !== 'undefined' && navigator.language) || 'en-us'

  const minimumFractionDigits = stripTrailingZeroes && amount % 100 === 0 ? 0 : 2

  if (currency === undefined) {
    return new Intl.NumberFormat(resolvedLocale, {
      minimumFractionDigits,
    }).format(amount / 100)
  }

  return new Intl.NumberFormat(resolvedLocale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits,
  }).format(amount / 100)
}

// Locales we serve but can't sell in are UK-entity pages, so they
// fall back to the UK storefront rather than resolving to a country checkout
// cannot serve.
export const getCheckoutCountryForLocale = (locale: WebsiteLocale): CheckoutCountry => {
  const country = localeToCountryCode(locale)
  return CHECKOUT_COUNTRIES.find((checkoutCountry) => checkoutCountry === country) ?? CHECKOUT_GB
}

export const getCurrencyCodeForLocale = (locale: WebsiteLocale): CurrencyCode =>
  COUNTRY_CURRENCY[getCheckoutCountryForLocale(locale)]

export const getCheckoutCountryForCurrency = (currency: CurrencyCode): CheckoutCountry | undefined =>
  CHECKOUT_COUNTRIES.find((country) => COUNTRY_CURRENCY[country] === currency.toLowerCase())
